/**
 * Checkout Manager Module
 * Handles checkout process and payment provider coordination
 */

import { getStorage, clearStorage } from '../core/storage.js';
import { KlarnaProvider } from './providers/klarna.js';
import { BamboraProvider } from './providers/bambora.js';
import { StripeProvider } from './providers/stripe.js';
import { NoneProvider } from './providers/none.js';
import { DiscountManager } from '../modules/discount.js';
import { GiftCardPaymentManager } from '../modules/gift-card-payment.js';
import { t } from '../core/i18n.js';
import { getCartType } from '../core/utils.js';

export class CheckoutManager {
    constructor(system) {
        this.system = system;
        this.config = {
            // Endast fallback-värden, system.config.checkout ska överskriva
            provider: 'klarna',
            purchaseCountry: 'se',
            purchaseCurrency: 'SEK',
            locale: 'sv-SE',
            apiUrl: '/cart/api/v1/order',
            confirmApiUrl: '/cart/api/v1/order/confirm',
            ...system.config.checkout  // Detta överskriver allt ovan
        };
        
        this.providers = {};
        this.discountManager = null;
        this.giftCardPaymentManager = null;
        this.initializeProviders();
    }

    initializeProviders() {
        // Initiera alla tillgängliga payment providers
        this.providers.klarna = new KlarnaProvider(this.config, this.system);
        this.providers.bambora = new BamboraProvider(this.config, this.system);
        this.providers.stripe = new StripeProvider(this.config, this.system);
        this.providers.none = new NoneProvider(this.config, this.system);
    }

    getCurrentProvider() {
        const providerName = this.config.provider || 'klarna';
        return this.providers[providerName] || this.providers.klarna;
    }

    setProvider(providerName) {
        if (this.providers[providerName]) {
            this.config.provider = providerName;
            return true;
        }
        return false;
    }

    init() {
        // PRIORITY 1: Check URL parameters for return from payment FIRST
        // This must run on ALL pages, not just checkout pages
        const urlParams = new URLSearchParams(window.location.search);
        const orderId = urlParams.get('orderId');
        const status = urlParams.get('status');
        
        if (orderId && status) {
            console.log('CheckoutManager: Detected return from payment with orderId and status');
            this.handleReturnFromPayment(orderId, status);
            // Don't return here - we may need to initialize checkout elements too
        } else if (orderId) {
            // Klarna-style confirmation (no explicit status parameter)
            console.log('CheckoutManager: Detected orderId without status (Klarna-style)');
            this.confirmOrder(orderId);
            
            // Backup: Clear cart on thank you page even without status parameter
            // This handles cases where the payment was successful but status wasn't passed
            this.ensureCartClearedOnThankYouPage();
        }
        
        // Additional backup: Clear cart if we're on a thank you page (detected by URL pattern)
        this.ensureCartClearedOnThankYouPage();
        
        // PRIORITY 2: Check if any checkout elements are present
        const klarnaContainer = document.getElementById('klarna');
        const customerForm = document.getElementById('customer-form');
        const paymentContainer = document.getElementById('payment-container');
        const discountSection = document.getElementById('discount-section');
        
        // Early return if no checkout elements found - this page doesn't have checkout
        if (!klarnaContainer && !customerForm && !paymentContainer && !discountSection) {
            console.log('CheckoutManager: No checkout elements found, skipping checkout initialization');
            return;
        }

        console.log('CheckoutManager: Initializing checkout functionality');
        
        // NOTE: All payment providers now use customer-form.js approach
        // No provider-specific initialization needed here

        // Initialize discount manager AFTER other elements are set up
        // Use setTimeout to ensure DOM is fully ready
        setTimeout(() => {
            this.initDiscountManager();
            this.initGiftCardPaymentManager();
        }, 100);
    }

    // NOTE: initKlarnaCheckout() removed - all providers use customer-form.js approach
    // NOTE: initCustomerFormCheckout() removed - handled by customer-form.js

    async initiateTestPayment(orderData) {
        try {
            // Samma API som Bambora men med None provider
            const response = await fetch('/cart/api/v1/order', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(orderData)
            });
            
            const result = await response.json();
            
            if (result.htmlSnippet) {
                const noneContainer = document.getElementById('none-checkout');
                if (noneContainer) {
                    noneContainer.innerHTML = result.htmlSnippet;
                    this.executeScripts(noneContainer);
                }
            }
        } catch (error) {
            console.error('Test payment initiation failed:', error);
            this.showError('Kunde inte initiera test-betalning. Försök igen.');
        }
    }

    handleReturnFromPayment(orderId, status) {
        if (status === 'success') {
            // Clear cart and redirect to thank you page
            clearStorage(this.system.config.storageKey);
            window.location.href = this.config.confirmUrl;
        } else if (status === 'cancel') {
            // Payment cancelled - cart is preserved, user is on checkout page
            console.log('Payment cancelled for order:', orderId);
            this.showError(t('checkout.paymentCancelled'));
            
            // Optional: Call API to mark order as aborted in database
            // This prevents abandoned orders from cluttering the system
            this.markOrderAsAborted(orderId);
        } else {
            // Unknown status - handle as error
            console.error('Unknown payment status:', status, 'for order:', orderId);
            this.showError(t('checkout.error'));
        }
    }

    async markOrderAsAborted(orderId) {
        try {
            console.log('Marking order as abortled:', orderId);
            
            const response = await fetch(`/cart/api/v1/order/abort/${orderId}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' }
            });
            
            if (response.ok) {
                const result = await response.json();
                console.log('Order successfully marked as aborted:', result);
            } else {
                const errorText = await response.text();
                console.warn('Failed to mark order as aborted:', response.status, errorText);
                // Non-critical - order will be cleaned up by background job if needed
            }
        } catch (error) {
            console.error('Failed to mark order as aborted:', error);
            // Non-critical error - don't show to user
        }
    }

    /**
     * Backup method to ensure cart is cleared on thank you pages
     * This provides a safety net in case cart wasn't cleared during payment flow
     */
    ensureCartClearedOnThankYouPage() {
        // Check if we're on a thank you page by looking for common patterns in URL
        const currentPath = window.location.pathname.toLowerCase();
        const isThankYouPage = currentPath.includes('thank-you') || 
                              currentPath.includes('tack-for-din-bestallning') ||
                              currentPath.includes('order-confirmation');
        
        if (isThankYouPage) {
            const cartItems = getStorage(this.system.config.storageKey);
            if (cartItems && cartItems.length > 0) {
                console.log('Backup: Clearing cart on thank you page (safety net)');
                clearStorage(this.system.config.storageKey);
            }
        }
    }

    executeScripts(container) {
        const scriptTags = container.getElementsByTagName('script');
        for (let i = 0; i < scriptTags.length; i++) {
            const parentNode = scriptTags[i].parentNode;
            const newScriptTag = document.createElement('script');
            newScriptTag.type = 'text/javascript';
            newScriptTag.text = scriptTags[i].text;
            parentNode.removeChild(scriptTags[i]);
            parentNode.appendChild(newScriptTag);
        }
    }

    async initCheckout() {
        try {
            const order = this.buildOrder();
            console.log('Sending order with provider:', this.config.provider, order);
            
            const provider = this.getCurrentProvider();
            const response = await provider.processOrder(order);
            
            if (response.success !== false) {
                provider.renderCheckout(response);
                
                // NOTE: Cart is NOT cleared here - only cleared on successful payment completion
                // This allows customer to return to checkout if they cancel the payment
                // Cart will be cleared in handleReturnFromPayment() when status === 'success'
            } else {
                throw new Error(response.message || 'Checkout failed');
            }
        } catch (error) {
            console.error('Checkout error:', error);
            this.showError(t('checkout.error'), error);
        }
    }

    buildOrder() {
        // Start with cart items as OrderRows
        let orderRows = getStorage(this.system.config.storageKey).map(product => {
            const orderRow = {
                ...product
            };
            
            // Include giftCardData if this is a gift card
            if (product.isGiftCard && product.giftCardData) {
                orderRow.giftCardData = product.giftCardData;
            }
            
            return orderRow;
        });

        // Determine cart type for GiftCard handling
        const cartItems = getStorage(this.system.config.storageKey) || [];
        const cartType = getCartType(cartItems);
        
        console.log('CHECKOUT DEBUG: Cart type for order building:', cartType);

        // Add shipping as OrderRow ONLY if NOT only gift cards
        if (cartType !== 'only-giftcards' && this.system.shippingOptions) {
            const shippingData = this.system.shippingOptions.getShippingData();
            console.log('CHECKOUT DEBUG: ShippingData from ShippingOptionsManager:', shippingData);
            
            if (shippingData && shippingData.shippingPrice > 0) {
                const shippingRow = {
                    //ProductNr: `SHIPPING-${shippingData.shippingOptionId}`,
                    ProductName: shippingData.shippingOptionName || 'Leverans',
                    ProductPrice: parseFloat(shippingData.shippingPrice),
                    TaxRate: parseFloat(shippingData.shippingTaxRate) || 25,
                    OrderCount: 1,
                    OrderUnit: 'st',
                    RowSum: parseFloat(shippingData.shippingPrice),
                    ProductUrl: '',
                    ImageUrl: '',
                    Type: 'ShippingFee'
                };
                
                orderRows.push(shippingRow);
                console.log('CHECKOUT DEBUG: Added shipping as OrderRow:', shippingRow);
            } else {
                console.log('CHECKOUT DEBUG: No shipping data or shipping cost is 0, not adding shipping OrderRow');
            }
        } else if (cartType === 'only-giftcards') {
            console.log('CHECKOUT DEBUG: Only GiftCards in cart - skipping shipping');
        } else {
            console.log('CHECKOUT DEBUG: No ShippingOptionsManager available');
        }

        // Helper function to ensure orderId parameter is appended to ConfirmUrl
        const ensureOrderIdParam = (confirmUrl) => {
            if (!confirmUrl) return confirmUrl;
            
            // Check if orderId parameter is already present
            if (confirmUrl.includes('orderId={checkout.order.id}')) {
                return confirmUrl;
            }
            
            // Add orderId parameter
            const separator = confirmUrl.includes('?') ? '&' : '?';
            return confirmUrl + separator + 'orderId={checkout.order.id}';
        };

        // CENTRALIZED: Include discount data for ALL providers
        let baseOrder = {
            MerchantId: this.config.merchantId,
            PurchaseCountry: this.config.purchaseCountry,
            PurchaseCurrency: this.config.purchaseCurrency,
            Locale: this.config.locale,
            OrderRows: orderRows
        };

        // CLEAN: Use DiscountManager's includeInOrder method ONLY if NOT only gift cards
        // This prevents discount abuse by excluding GiftCards from discount calculations
        if (cartType !== 'only-giftcards' && this.discountManager) {
            console.log('CHECKOUT DEBUG: Using DiscountManager.includeInOrder() for clean separate discounts');
            baseOrder = this.discountManager.includeInOrder(baseOrder);
        } else if (cartType === 'only-giftcards') {
            console.log('CHECKOUT DEBUG: Only GiftCards in cart - skipping discounts to prevent abuse');
        } else {
            console.log('CHECKOUT DEBUG: No DiscountManager available');
        }

        // Bygg order baserat på provider
        if (this.config.provider === 'klarna') {
            return {
                ...baseOrder,
                TermsUrl: this.config.termsUrl,
                CheckoutUrl: this.config.checkoutUrl,
                ConfirmUrl: ensureOrderIdParam(this.config.confirmUrl),
                options: {
                    show_subtotal_detail: true
                }
            };
        } else if (this.config.provider === 'bambora') {
            return {
                ...baseOrder,
                PaymentProvider: 'bambora',
                TermsUrl: this.config.termsUrl,
                CheckoutUrl: this.config.checkoutUrl,
                ConfirmUrl: ensureOrderIdParam(this.config.confirmUrl),
                BamboraSettings: {
                    currency: this.config.purchaseCurrency,
                    language: this.config.locale.split('-')[0],
                    paymentMethods: this.config.bamboraPaymentMethods || ['card', 'mobilepay']
                }
            };
        } else if (this.config.provider === 'none') {
            return {
                ...baseOrder,
                PaymentProvider: 'none',
                TestMode: true
            };
        }

        // Fallback till Klarna-format
        return {
            ...baseOrder,
            TermsUrl: this.config.termsUrl,
            CheckoutUrl: this.config.checkoutUrl,
            ConfirmUrl: ensureOrderIdParam(this.config.confirmUrl),
            options: {
                show_subtotal_detail: true
            }
        };
    }

    async confirmOrder(orderId) {
        try {
            const provider = this.getCurrentProvider();
            const data = await provider.confirmOrder(orderId);
            
            if (data.htmlSnippet || data.success) {
                provider.renderCheckout(data);
            }
        } catch (error) {
            console.error('Order confirmation error:', error);
            this.showError(t('checkout.error'), error);
        }
    }

    showError(message, error = null) {
        const provider = this.getCurrentProvider();
        provider.showError(message, error);
    }

    // Publika metoder för att växla payment provider
    switchToKlarna() {
        return this.setProvider('klarna');
    }

    switchToBambora() {
        return this.setProvider('bambora');
    }

    switchToTestMode() {
        return this.setProvider('none');
    }

    getAvailableProviders() {
        return Object.keys(this.providers);
    }

    getCurrentProviderType() {
        return this.config.provider;
    }

    // Discount Manager Integration
    initDiscountManager() {
        const discountSection = document.getElementById('discount-section');
        if (discountSection) {
            console.log('CheckoutManager: Initializing discount manager');
            this.discountManager = new DiscountManager(this.system);
            this.discountManager.init();
            
            // Expose DiscountManager globally for BamboraProvider access
            if (window.ecommerce) {
                if (!window.ecommerce.checkout) {
                    window.ecommerce.checkout = {};
                }
                window.ecommerce.checkout.discountManager = this.discountManager;
                console.log('CheckoutManager: DiscountManager exposed globally for BamboraProvider');
            }
            
            // Listen for discount updates to recalculate totals
            document.addEventListener('discountUpdated', (event) => {
                this.handleDiscountUpdate(event.detail);
            });
        }
    }

    // Gift Card Payment Manager Integration (Phase 3)
    initGiftCardPaymentManager() {
        this.giftCardPaymentManager = new GiftCardPaymentManager(this.system);
        this.giftCardPaymentManager.init();

        // Expose globally - the providers read giftCardPayments from here in
        // buildOrderData (same pattern as discountManager), and the central
        // total calculation reads the deduction from here
        if (window.ecommerce) {
            if (!window.ecommerce.checkout) {
                window.ecommerce.checkout = {};
            }
            window.ecommerce.checkout.giftCardPaymentManager = this.giftCardPaymentManager;
        }
    }

    handleDiscountUpdate(discountData) {
        console.log('CheckoutManager: Discount updated', discountData);
        // The DiscountManager handles UI updates automatically
        // This is where we could add additional logic if needed
    }


    /**
     * Update totals when discount stack changes (new method for multi-discount support)
     */
    updateTotals() {
        console.log('CheckoutManager: Updating totals after discount stack change');
        
        // This method is called by the ecommerce-system.js event handler
        // The DiscountManager handles the actual UI updates
        // We could add additional checkout-specific logic here if needed
        
        // Dispatch event to notify other components
        const event = new CustomEvent('checkoutTotalsUpdated', {
            detail: {
                discountManager: this.discountManager,
                totalDiscountAmount: this.discountManager?.getTotalDiscountAmount?.() || 0
            }
        });
        document.dispatchEvent(event);
    }
}
